import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FilterState } from '../../types'
import { Button } from '../ui/Button'
import { formatNumber } from '../../utils/formatters'
import { autoModeApi } from '../../services/api'
import { DateFilterComponent } from '../filters/DateFilterComponent'

// Commodity mapping: Display Name -> Backend Code
const COMMODITY_MAPPING = {
  "Silver": "Ag", "Aluminum": "Al", "Alumina": "Al2O3", "Arsenic": "As", "Gold": "Au", "Boron": "B", "Barium": "Ba", "Bauxite": "Bauxite (Al2O3)", "Beryllium": "Be", "Bismuth": "Bi", "Calcium Carbonate": "CaCO3", "Cadmium": "Cd", "Cobalt": "Co", "Coal": "Coal", "Chromium": "Cr", "Cesium": "Cs", "Copper": "Cu", "Diamond": "Diamond", "Iron Ore": "Fe", "Fluorite": "Fluorite", "Gallium": "Ga", "Garnet": "Garnet", "Germanium": "Ge", "Graphite": "Graphite", "Halloysite": "Halloysite", "Helium": "He", "Heavy Minerals": "Heavy Minerals", "Heavy Sands": "Heavy Sands", "Hafnium": "Hf", "Mercury": "Hg", "Hydrogen": "Hydrogen", "Ilmenite": "Ilmenite", "Indium": "In", "Iridium": "Ir", "Kaolin": "Kaolin", "Kaolinite": "Kaolinite", "Lanthanum": "La", "Lithium": "Li", "Magnesite": "Magnesite", "Magnetite": "Magnetite", "Magnesium": "Mg", "Mineral Sands": "Mineral Sands", "Manganese": "Mn", "Molybdenum": "Mo", "Salt (NaCl)": "NaCl", "Niobium": "Nb", "Neodymium": "Nd", "Nickel": "Ni", "Phosphorus": "P", "PGE": "PGE", "PGM": "PGM", "Lead": "Pb", "Palladium": "Pd", "Phosphate": "Phosphate", "Platinum": "Pt", "REE": "REE", "Rubidium": "Rb", "Rhenium": "Re", "Rhodium": "Rh", "Rutile": "Rutile", "Sulfur": "S", "Salt": "Salt", "Antimony": "Sb", "Scandium": "Sc", "Selenium": "Se", "Silicon": "Si", "Silica": "SiO2", "Tin": "Sn", "Strontium": "Sr", "TREO": "TREO", "Tantalum": "Ta", "Terbium": "Tb", "Tellurium": "Te", "Thorium": "Th", "Titanium": "Ti", "Thallium": "Tl", "Uranium": "U", "Uranium Oxide": "U3O8", "Vanadium": "V", "VMS": "VMS", "Tungsten": "W", "Zinc": "Zn", "Zirconium": "Zr"
}

// Get display names for the UI
const COMMODITIES = Object.keys(COMMODITY_MAPPING).sort()

// Countries - exact list from JavaScript filter file
const COUNTRIES = [
  "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Côte d'Ivoire", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia", "Czech Republic", "Democratic Republic of the Congo", "DRC", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Holy See", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine State", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "UAE", "United Kingdom", "United States of America", "USA", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
]

// States/Provinces - exact list from JavaScript filter file
const STATES = [
  "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "Wisconsin", "Wyoming", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon"
]

// Article Types organized by category - exact from JavaScript filter file
const ARTICLE_TYPES = {
  drilling: [
    "Drilling Update", "Drilling Commenced", "Drill Permits", "Drilling Completed", "Drill Program", "Drill Results", "Planned Drilling"
  ],
  'mergers-acquisitions': [
    "Mergers & Acquisitions", "Joint Venture", "Project Acquisition"
  ],
  'company-project-updates': [
    "Permitting", "Project Update", "Company Update", "ESG", "Options Grant", "Royalties and Streams", "Exploration update", "Sampling Results", "First Nations", "Construction Update", "Production Results", "Exporation and Development", "Bulk Sample", "Geophysics Program", "Geophysical Survey", "Sustainability Report", "Management Changes", "AGM", "Spin Out", "Arbitration"
  ],
  financings: [
    "Financials", "Earnings Notice", "Private Placement", "Financing"
  ],
  'resource-estimate': [
    "Resource Estimate", "Pre-Feasibility Study", "Feasibility Study", "NI 43-101 (Geology Report)", "Preliminary Economic Assessment"
  ]
}

interface FilterPanelProps {
  onFiltersChange: (filters: FilterState) => void
  onFetchNews: () => void
  isLoading?: boolean
  filtersVisible?: boolean
  onToggleFiltersVisible?: () => void
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ 
  onFiltersChange, 
  onFetchNews, 
  isLoading = false,
  filtersVisible = true,
  onToggleFiltersVisible
}) => {
  const [autoLoading, setAutoLoading] = useState(false)

  const { register, watch, reset, setValue, getValues } = useForm<FilterState>({
    defaultValues: {
      country: '',
      state: '',
      area: '',
      commodity: [],
      type: [],
      project: '',
      company: '',
      ticker: '',
      article: '',
      'free-text-search': '',
      dateFilter: { preset: 'today' },
      countryWhere: 'all',
      stateWhere: 'all',
      areaWhere: 'all',
      commoditiesWhere: 'all',
      marketcap: { min: 0, max: 10000 },
      outstandingshares: { min: 0, max: 10000 },
      projectSize: [null, null]
    }
  })

  // Get current form values for display
  const currentValues = watch()

  // Initialize filters in parent component on mount
    useEffect(() => {
    onFiltersChange(getValues())
  }, []) // Changed dependency to run only once


  // Handle loading auto configuration into main form
  const handleLoadAutoConfiguration = async () => {
      setAutoLoading(true)
      try {
      console.log('Loading auto configuration...')
      const autoFilters = await autoModeApi.getAutoFilters()
      console.log('Auto filters loaded:', autoFilters)
      
      // Populate main form with auto configuration
      reset(autoFilters)
      console.log('Auto configuration loaded into main form successfully')
      } catch (error) {
      console.error('Failed to load auto configuration:', error)
      } finally {
        setAutoLoading(false)
      }
    }

  // Handle saving current filters as auto configuration
  const handleSaveAutoConfiguration = async () => {
    setAutoLoading(true)
    try {
      console.log('Saving current filters as auto configuration...')
      const currentFilters = getValues()
      await autoModeApi.saveAutoFilters(currentFilters)
      console.log('Auto configuration saved successfully')
    } catch (error) {
      console.error('Failed to save auto configuration:', error)
    } finally {
      setAutoLoading(false)
    }
  }

  // Notify parent of filter changes
  useEffect(() => {
    const subscription = watch((value) => {
        onFiltersChange(value as FilterState)
    })
    return () => subscription.unsubscribe()
  }, [watch, onFiltersChange])

  const handleCommodityChange = (commodity: string, checked: boolean) => {
    const current = getValues('commodity') || []
    if (checked) {
      setValue('commodity', [...current, commodity])
    } else {
      setValue('commodity', current.filter(c => c !== commodity))
    }
  }

  const handleTypeChange = (type: string, checked: boolean) => {
    const current = getValues('type') || []
    if (checked) {
      setValue('type', [...current, type])
    } else {
      setValue('type', current.filter(t => t !== type))
    }
  }

  const handleSelectAllCommodities = (checked: boolean) => {
    if (checked) {
      setValue('commodity', COMMODITIES)
    } else {
      setValue('commodity', [])
    }
  }

  const handleSelectAllArticleTypes = (category: keyof typeof ARTICLE_TYPES, checked: boolean) => {
    const currentTypes = getValues('type') || []
    const categoryTypes = ARTICLE_TYPES[category]

    let newTypes: string[]
    if (checked) {
      newTypes = Array.from(new Set([...currentTypes, ...categoryTypes]))
    } else {
      newTypes = currentTypes.filter(t => !categoryTypes.includes(t))
    }
    setValue('type', newTypes)
  }

  const resetFilters = () => {
    reset({
      country: '',
      state: '',
      area: '',
      commodity: [],
      type: [],
      project: '',
      company: '',
      ticker: '',
      article: '',
      'free-text-search': '',
      dateFilter: { preset: 'today' },
      countryWhere: 'all',
      stateWhere: 'all',
      areaWhere: 'all',
      commoditiesWhere: 'all',
      marketcap: { min: 0, max: 10000 },
      outstandingshares: { min: 0, max: 10000 },
      projectSize: [null, null]
    })
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="p-6">
        {/* Header with action buttons */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            News Filters
          </h2>
          <div className="flex items-center space-x-3">
            <Button 
              variant="success" 
              onClick={onFetchNews}
              loading={isLoading}
              disabled={isLoading}
            >
              Fetch News
            </Button>
            <Button 
              variant="secondary" 
              onClick={resetFilters}
            >
              Reset All
            </Button>
            <Button 
              variant="secondary" 
              onClick={onToggleFiltersVisible}
            >
              {filtersVisible ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleLoadAutoConfiguration}
              disabled={autoLoading}
            >
              {autoLoading ? 'Loading...' : 'Load Auto Configuration'}
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleSaveAutoConfiguration}
              disabled={autoLoading}
            >
              {autoLoading ? 'Saving...' : 'Save as Auto Configuration'}
            </Button>
          </div>
        </div>

        {/* Filter Summary */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            {Object.values(currentValues).some(v => Array.isArray(v) ? v.length > 0 : !!v) && (
              <div className="text-sm">
          <span className="font-medium text-gray-700">Active Filters: </span>
          <span className="text-gray-600">
                  {currentValues.dateFilter?.preset !== 'today' && `${currentValues.dateFilter?.preset.replace(/-/g, ' ')}, `}
                  {currentValues.commodity?.length > 0 && `${formatNumber(currentValues.commodity?.length)} commodities, `}
                  {currentValues.type?.length > 0 && `${formatNumber(currentValues.type?.length)} types`}
          {currentValues.country && `, ${currentValues.country}`}
                  {currentValues['free-text-search'] && `, "${currentValues['free-text-search']}"`}
          </span>
              </div>
            )}
          </div>
        </div>

        {/* Filter Content */}
        {filtersVisible && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Date Range */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <DateFilterComponent
                  value={currentValues.dateFilter || { preset: 'today' }}
                  onChange={(dateFilter) => setValue('dateFilter', dateFilter)}
                />
              </div>

              {/* Location */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Location
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select 
                      {...register('country')}
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900"
                    >
                      <option value="">All Countries</option>
                      {COUNTRIES.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                    <select 
                      {...register('state')}
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900"
                    >
                      <option value="">All States/Provinces</option>
                      {STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                    <input
                      type="text"
                      {...register('area')}
                      placeholder="Enter area"
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Search Terms */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Search Terms
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Free Text Search</label>
                    <input
                      type="text"
                      {...register('free-text-search')}
                      placeholder="Enter keywords..."
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input
                      type="text"
                      {...register('company')}
                      placeholder="Company name..."
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                    <input
                      type="text"
                      {...register('project')}
                      placeholder="Project name..."
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticker</label>
                    <input
                      type="text"
                      {...register('ticker')}
                      placeholder="Stock ticker..."
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Article</label>
                    <input
                      type="text"
                      {...register('article')}
                      placeholder="Article reference..."
                      className="w-full rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Market Filters */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Market Cap Range (Million $)
                </h3>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    {...register('marketcap.min', { valueAsNumber: true })}
                    placeholder="$0M"
                    min="0"
                    className="flex-1 rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                  />
                  <span className="text-gray-600 font-medium">to</span>
                  <input
                    type="number"
                    {...register('marketcap.max', { valueAsNumber: true })}
                    placeholder="$10,000M"
                    min="0"
                    className="flex-1 rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Outstanding Shares (Million)
                </h3>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    {...register('outstandingshares.min', { valueAsNumber: true })}
                    placeholder="0M"
                    min="0"
                    className="flex-1 rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                  />
                  <span className="text-gray-600 font-medium">to</span>
                  <input
                    type="number"
                    {...register('outstandingshares.max', { valueAsNumber: true })}
                    placeholder="10,000M"
                    min="0"
                    className="flex-1 rounded-lg border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>
            </div>

            {/* Commodities and Article Types Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Commodities */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Commodities
                </h3>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto bg-white border border-gray-300 rounded-lg p-3">
                  <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded transition-colors font-semibold">
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAllCommodities(e.target.checked)}
                      checked={(currentValues.commodity?.length || 0) === COMMODITIES.length}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-800">Select All</span>
                  </label>
                  {COMMODITIES.map(commodity => (
                    <label key={commodity} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={currentValues.commodity?.includes(commodity) || false}
                        onChange={(e) => handleCommodityChange(commodity, e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                      />
                      <span className="text-sm text-gray-800">{commodity}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Article Types */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  Article Types
                </h3>
                <div className="max-h-64 overflow-y-auto bg-white border border-gray-300 rounded-lg p-3">
                  {Object.entries(ARTICLE_TYPES).map(([category, types]) => (
                    <div key={category} className="mb-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h4>
                      <div className="grid grid-cols-1 gap-1">
                        <label className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded transition-colors font-semibold">
                          <input
                            type="checkbox"
                            onChange={(e) => handleSelectAllArticleTypes(category as keyof typeof ARTICLE_TYPES, e.target.checked)}
                            checked={types.every(t => (currentValues.type || []).includes(t))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                          />
                          <span className="text-sm text-gray-800">Select All</span>
                        </label>
                        {types.map(type => (
                          <label key={type} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={currentValues.type?.includes(type) || false}
                              onChange={(e) => handleTypeChange(type, e.target.checked)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-800">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 